import { cloneElement, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { TransitionGroup } from 'react-transition-group';
import { Typography, Fade, makeStyles } from '@material-ui/core';
import DraggableScroll from '../draggableScroll';
import DraggableTabs from '../draggableTabs';
import NotificationCard from '../notificationCard';
import SectionLoader from '../sectionLoader';
import ScrollIndicators from '../scrollIndicators';
import { primaryPalette } from '../../../styles/theme';
import {
  isArrayEmpty,
  asyncDebounce,
  getUnionOfTwoArrays,
} from '../../../utility/helpers';
import {
  useCustomEffect,
  usePreviousValue,
  useDraggableScrollPosition,
} from '../../../utility/hooks';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  root: {
    backgroundColor: primary.bluish8,
    borderRadius: 8,
    height: ({ isVertical }) => (isVertical ? '100%' : '260px'),
    borderLeft: ({ color }) => `3px solid ${color}`,
    position: 'relative',
  },
  header: {
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: spacing(0, 6),
    marginBottom: spacing(4),
    height: 56,
  },
  headerWithTabs: {
    borderBottom: `1px solid ${primary.bluish7}`,
  },
  subHeader: {
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: spacing(0, 6),
    height: 48,
  },
  content: {
    position: 'relative',
    height: 'calc(100% - 56px - 16px)', // subtract heigh and margin of header
  },
  contentWithSubHeader: {
    height: 'calc(100% - 56px - 16px - 48px)', // subtract heigh and margin of header + height of subHeader
  },
  scrollWrapper: {
    display: 'flex',
    height: ({ isVertical }) => (isVertical ? '100%' : 'auto'),
  },
  itemsList: {
    width: ({ isVertical }) => (isVertical ? '100%' : 'auto'),
  },
  itemsWrapper: {
    display: 'flex',
    flexDirection: ({ isVertical }) => (isVertical ? 'column' : 'row'),
    width: ({ isVertical }) => (isVertical ? '100%' : 'auto'),
  },
  itemWrapper: {
    boxSizing: 'border-box',
    padding: spacing(0.5, 0),
    marginRight: spacing(4),
    '&:first-of-type': {
      marginLeft: spacing(6),
    },
    '&:last-of-type': {
      marginRight: spacing(6),
    },
  },
  itemWrapperVertical: {
    boxSizing: 'border-box',
    padding: spacing(0, 6),
    marginBottom: spacing(4),
    '&:first-of-type': {
      paddingTop: spacing(0.5),
    },
    '&:last-of-type': {
      marginBottom: 0,
      paddingBottom: spacing(4),
    },
  },
  item: {
    boxShadow: `0px 2px 8px 0px rgba(0, 0, 0, 0.07)`,
  },
  noResults: {
    position: 'absolute',
    left: '50%',
    top: 'calc(50% - 16px)', // middle of content - header margin
    transform: 'translate(-50%, -50%)',
    padding: spacing(7.5, 6),
  },
  noResultsIcon: {
    height: 22,
    width: 'auto',
  },
  actionsWrapper: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
  },
}));

const DashboardSection = ({
  className = undefined,
  headerClass = undefined,
  contentClass = undefined,
  scrollWrapperClass = undefined,
  scrollArrowClass = undefined,
  noResultsClass = undefined,
  subHeaderClass = undefined,
  translations,
  isHeaderLoading = true,
  isContentLoading,
  color = primaryPalette.bluish1,
  items = [],
  itemComponent: Component = () => {},
  itemPropKey,
  currentPage = 1,
  tabs = [],
  activeTab = null,
  onTabSelect = () => {},
  actionButtons = [],
  subHeaderActionButtons = [],
  sectionIcon: SectionIcon,
  isVertical = false,
  isNotificationDisabled = false,
  hasLoadMore = false,
  hasSubHeader = false,
  hasTabs = false,
  onLoadMoreItems = () => {},
  ...rest
}) => {
  const scrollRef = useRef(null);
  const itemsRef = useRef(null);
  const hasItems = !isArrayEmpty(items);
  const hasActions = !isArrayEmpty(actionButtons);
  const hasSubHeaderActions = !isArrayEmpty(subHeaderActionButtons);
  const classes = useStyles({ color, isVertical });
  const previousItems = usePreviousValue(items);
  const [
    activeScroll,
    onChangeScrollPosition,
    onScrollToRight,
    onScrollToBottom,
    onScrollToStart,
  ] = useDraggableScrollPosition(scrollRef, itemsRef);

  useCustomEffect(
    () => {
      if (
        currentPage === 1 &&
        scrollRef?.current &&
        getUnionOfTwoArrays(previousItems, items).length !== items.length
      ) {
        scrollRef?.current.scrollTo({ left: 0, top: 0, behavior: 'smooth' });
      }
    },
    [currentPage, previousItems],
    false
  );

  const handleLoadMore = useCallback(() => {
    if (scrollRef?.current) {
      const {
        clientWidth,
        scrollWidth,
        scrollLeft,
        scrollTop,
        scrollHeight,
        clientHeight,
      } = scrollRef.current;
      const isEndOfContainer = isVertical
        ? scrollHeight - scrollTop - clientHeight <= 32
        : scrollWidth - scrollLeft - clientWidth <= 32;
      onChangeScrollPosition();

      if (hasLoadMore && isEndOfContainer) {
        return onLoadMoreItems();
      }
    }
  }, [isVertical, hasLoadMore, onLoadMoreItems, onChangeScrollPosition]);

  return (
    <Fade in unmountOnExit>
      <div className={classNames(classes.root, className)}>
        <div
          className={classNames(
            classes.header,
            { [classes.headerWithTabs]: hasTabs },
            headerClass
          )}
        >
          {!isArrayEmpty(tabs) ? (
            <DraggableTabs
              tabs={tabs}
              activeTab={activeTab}
              isVisible={!isHeaderLoading}
              onTabSelect={onTabSelect}
            />
          ) : (
            <Fade in={!isHeaderLoading} unmountOnExit>
              <Typography variant="h5">{translations.title}</Typography>
            </Fade>
          )}
          <Fade in={hasActions && !isHeaderLoading} unmountOnExit>
            <div className={classes.actionsWrapper}>
              {actionButtons.map(aButton => {
                const { id, button: Button, text, ...actionProps } = aButton;
                return text ? (
                  <Button key={`action_button_${id}`} {...actionProps}>
                    {text}
                  </Button>
                ) : (
                  <Button key={`action_button_${id}`} {...actionProps} />
                );
              })}
            </div>
          </Fade>
        </div>
        {hasSubHeader && (
          <div className={classNames(classes.subHeader, subHeaderClass)}>
            <Fade in={hasSubHeaderActions && !isHeaderLoading} unmountOnExit>
              <div className={classes.actionsWrapper}>
                {subHeaderActionButtons.map(shActionButton => {
                  const {
                    id,
                    button: Button,
                    text,
                    ...subHeaderActionProps
                  } = shActionButton;
                  return text ? (
                    <Button
                      key={`sub_header_action_button_${id}`}
                      {...subHeaderActionProps}
                    >
                      {text}
                    </Button>
                  ) : (
                    <Button
                      key={`sub_header_action_button_${id}`}
                      {...subHeaderActionProps}
                    />
                  );
                })}
              </div>
            </Fade>
          </div>
        )}
        <div
          className={classNames(
            classes.content,
            { [classes.contentWithSubHeader]: hasSubHeader },
            contentClass
          )}
        >
          <SectionLoader isLoading={isContentLoading} />
          <DraggableScroll
            ref={el => {
              scrollRef.current = el;
            }}
            className={classNames(classes.scrollWrapper, scrollWrapperClass)}
            onEndScroll={asyncDebounce(handleLoadMore)}
          >
            <div ref={itemsRef} className={classes.itemsList}>
              <TransitionGroup className={classes.itemsWrapper}>
                {items.map(item => (
                  <Fade
                    key={`item_${item.id}`}
                    className={classNames({
                      [classes.itemWrapper]: !isVertical,
                      [classes.itemWrapperVertical]: isVertical,
                    })}
                    in
                    unmountOnExit
                  >
                    <div>
                      {cloneElement(
                        <Component
                          className={classes.item}
                          translations={
                            translations?.[itemPropKey] || translations
                          }
                          {...rest}
                        />,
                        { [itemPropKey]: item }
                      )}
                    </div>
                  </Fade>
                ))}
              </TransitionGroup>
            </div>
          </DraggableScroll>
          <ScrollIndicators
            arrowLeftClass={scrollArrowClass}
            arrowRightClass={scrollArrowClass}
            activeScroll={activeScroll}
            onScrollToBottom={onScrollToBottom}
            onScrollToRight={onScrollToRight}
            onScrollToStart={onScrollToStart}
          />
          <NotificationCard
            className={classNames(classes.noResults, noResultsClass)}
            iconClass={classes.noResultsIcon}
            title={translations.noData.title}
            content={translations.noData.message}
            customIcon={SectionIcon}
            shouldFade={
              !isNotificationDisabled && !isContentLoading && !hasItems
            }
            hasIcon
          />
        </div>
      </div>
    </Fade>
  );
};

DashboardSection.propTypes = {
  className: PropTypes.string,
  headerClass: PropTypes.string,
  subHeaderClass: PropTypes.string,
  contentClass: PropTypes.string,
  scrollWrapperClass: PropTypes.string,
  scrollArrowClass: PropTypes.string,
  noResultsClass: PropTypes.string,
  translations: PropTypes.object.isRequired,
  isHeaderLoading: PropTypes.bool,
  isContentLoading: PropTypes.bool.isRequired,
  items: PropTypes.arrayOf(PropTypes.object),
  tabs: PropTypes.arrayOf(PropTypes.object),
  activeTab: PropTypes.object,
  sectionIcon: PropTypes.object.isRequired,
  itemComponent: PropTypes.func,
  itemPropKey: PropTypes.string.isRequired,
  isNotificationDisabled: PropTypes.bool,
  actionButtons: PropTypes.arrayOf(PropTypes.object),
  subHeaderActionButtons: PropTypes.arrayOf(PropTypes.object),
  currentPage: PropTypes.number,
  color: PropTypes.string,
  isVertical: PropTypes.bool,
  hasSubHeader: PropTypes.bool,
  hasTabs: PropTypes.bool,
  hasLoadMore: PropTypes.bool,
  onLoadMoreItems: PropTypes.func,
  onTabSelect: PropTypes.func,
};

export default DashboardSection;
